import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import type { IOperatorStationSlim } from "App/Modules/Models";
import { Button } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { faFilter, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { faHorizontalRule, faPlus } from "@fortawesome/pro-light-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { SearchField } from "App/ReactComponents/Shared";
import "./_list-section.scss";
import "./_sites-link-filter.scss";

type OperatorStationsLinkListProps = {
    operatorStations: IOperatorStationSlim[];
    addNewOperatorStation: (event) => void;
    editOperatorStation: (operatorStation: object) => void;
    isLoading: boolean;
    isOperatorStationsLoading: boolean;
};

export const OperatorStationsLinkList = (props: OperatorStationsLinkListProps) => {
    const { operatorStations, isLoading, isOperatorStationsLoading, addNewOperatorStation, editOperatorStation } =
        props;

    const [pageNumber, setPageNumber] = useState(1);
    const [stationsFilter, setstationsFilter] = useState("");
    const [active, setActive] = useState(false);
    const { t } = useTranslation();

    const increasePageNumber = () => {
        setPageNumber(pageNumber + 1);
    };

    const handleDisplaySearchFilter = (event: React.MouseEvent) => {
        event.preventDefault();
        setActive(!active);
    };

    const filterFunc = (station) => {
        if (!stationsFilter) {
            return true;
        }
        return station.serialNumber.toLowerCase().indexOf(stationsFilter.toLowerCase()) > -1;
    };

    const filteredOperatorStations = (operatorStations || []).filter((station) => filterFunc(station));

    return isLoading && isOperatorStationsLoading ? (
        <div className="list-section">
            <h2 className="title title--h2">{t("Entities.AlarmCentral.OperatorStations")}</h2>
            <div className="list-section__buttons">
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    disabled
                    startIcon={<FontAwesomeIcon icon={faPlus} className="list-section__button-icon" />}
                    onClick={addNewOperatorStation}
                >
                    {t("Gui.Common.AddNew")}
                </Button>
            </div>
            <ul className="entity-list">
                {Array.from(new Array(2)).map((d, i) => (
                    <li key={i} className="entity-list__item">
                        <div className="entity-list__item-link entity-list__item-link--button">
                            <div className="entity-list__item-panel">
                                <div className="entity-list__item-value">
                                    <Skeleton variant="text" animation="wave" height="16px" width="70%" />
                                    <Skeleton variant="text" animation="wave" height="12px" width="50%" />
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    ) : (
        <React.Fragment>
            {operatorStations ? (
                <div className="list-section">
                    <div className="list-section__title-heading">
                        <h2 className="title title--h2">{t("Entities.AlarmCentral.OperatorStations")}</h2>
                        {filteredOperatorStations?.length < operatorStations?.length ? (
                            <span className="caption">
                                <FontAwesomeIcon icon={faHorizontalRule} className="list-section__title-icon" />
                                {`${t("Gui.Manage.ShowingOfTotal", {
                                    showCount: filteredOperatorStations?.length,
                                    totalCount: operatorStations?.length,
                                })}`}
                            </span>
                        ) : (
                            <span className="caption">
                                <FontAwesomeIcon icon={faHorizontalRule} className="list-section__title-icon" />
                                {`${operatorStations?.length || 0} ${t("Gui.Common.AlarmCentralValue.Found")}`}
                            </span>
                        )}
                    </div>
                    <div className="list-section__buttons">
                        <Button
                            size="small"
                            color="default"
                            className="list-section__filter-btn"
                            onClick={handleDisplaySearchFilter}
                            startIcon={<FontAwesomeIcon icon={faFilter} className="list-section__button-icon" />}
                        >
                            {t("Gui.Common.Filter")}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            startIcon={<FontAwesomeIcon icon={faPlus} className="list-section__button-icon" />}
                            onClick={addNewOperatorStation}
                        >
                            {t("Gui.Common.AddNew")}
                        </Button>
                    </div>
                    {active ? (
                        <div className="sites-link-filter sites-link-filter--active">
                            <div className="sites-link-filter__search-input">
                                <SearchField globalFilter={stationsFilter} setGlobalFilter={setstationsFilter} />
                            </div>
                            <span className="caption">{t("Gui.Common.AlarmCentralOperatorStations.Search")}</span>
                        </div>
                    ) : null}

                    <ul className="entity-list">
                        {filteredOperatorStations.length === 0 ? (
                            <p className="entity-list__empty-list">
                                {t("Gui.Common.AlarmCentralOperatorStations.NotFound")}
                            </p>
                        ) : null}
                        {filteredOperatorStations?.map((station, stationIndex) => {
                            if (stationIndex >= pageNumber * 10) {
                                return null;
                            }
                            return (
                                <li className="entity-list__item" key={station.id}>
                                    <button
                                        type="button"
                                        className="entity-list__item-button"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            editOperatorStation(station);
                                        }}
                                    >
                                        <div className="entity-list__item-panel">
                                            <div className="entity-list__item-value">
                                                <p className="paragraph paragraph--bold">{station.serialNumber}</p>
                                                <p className="caption">
                                                    {`${station.equipmentTypeModel} - ${station.equipmentTypeVersion}`}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="entity-list__item-arrow">
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    {pageNumber * 10 <= operatorStations?.length ? (
                        <Button
                            variant="contained"
                            size="large"
                            color="primary"
                            className="list-section__show-more"
                            onClick={() => increasePageNumber()}
                        >
                            {t("Gui.Buttons.Show.More")}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </React.Fragment>
    );
};
